import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    serverTimestamp,
    Timestamp
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { db, auth, storage } from '../firebase/config';
import { onAuthStateChanged, getAuth } from 'firebase/auth';

// Note: adminService should be implemented separately or moved to this folder
// For now, we'll create a placeholder
const adminService = {
    deleteUserFromAuth: async (userId) => {
        // Implementation needed
        console.log('deleteUserFromAuth not implemented yet');
        return { success: false, message: 'Not implemented' };
    }
};

/**
 * Firestore + Auth combined service for Zinea Admin Interface
 * - Fetches Firestore user metadata
 * - Pulls Auth metadata (provider, createdAt, lastSignInTime)
 * - Handles audit logs + analytics
 */
class FirestoreService {
    // ✅ Wait for auth
    async waitForAuth() {
        return new Promise((resolve, reject) => {
            const unsubscribe = onAuthStateChanged(auth, (user) => {
                unsubscribe();
                if (user) {
                    resolve(user);
                } else {
                    reject(new Error('Not authenticated - please log in first'));
                }
            });
        });
    }

    /**
     * ✅ Get all users
     * Combines Firestore user data + Firebase Auth metadata (via Admin SDK endpoint)
     */
    async getUsers() {
        try {
            await this.waitForAuth();

            const usersRef = collection(db, 'users');
            const snapshot = await getDocs(usersRef);
            const users = [];

            for (const userDoc of snapshot.docs) {
                const data = userDoc.data();
                const now = new Date();

                // Initialize
                let isActive = true;
                let status = 'Active';
                let daysSinceActivity = 0;
                let lastActivityDate = null;

                // --- Handle date conversions safely ---
                const parseDate = (val) => {
                    if (!val) return null;
                    if (val.toDate && typeof val.toDate === 'function') return val.toDate();
                    if (val instanceof Date) return val;
                    return new Date(val);
                };

                const lastLogin = parseDate(data.lastLoginAt);
                const lastActivity = parseDate(data.lastActivityAt);
                const createdAt = parseDate(data.createdAt);

                // --- Compute activity status from audit logs ---
                const lastAppAccess = await this.getLastAppAccessFromAuditLogs(userDoc.id);

                if (lastAppAccess) {
                    lastActivityDate = lastAppAccess;
                    daysSinceActivity = Math.floor((now - lastAppAccess) / (1000 * 60 * 60 * 24));
                    if (daysSinceActivity > 60) {
                        isActive = false;
                        status = 'Inactive';
                    }
                } else {
                    // Fallback to old logic if no audit logs found
                    lastActivityDate = [lastActivity, lastLogin, createdAt]
                        .filter((d) => d && !isNaN(d.getTime()))
                        .sort((a, b) => b - a)[0];

                    if (lastActivityDate) {
                        daysSinceActivity = Math.floor((now - lastActivityDate) / (1000 * 60 * 60 * 24));
                        if (daysSinceActivity > 60) {
                            isActive = false;
                            status = 'Inactive';
                        }
                    } else {
                        isActive = false;
                        status = 'Inactive';
                    }
                }

                // --- Get Firebase Auth data via Admin SDK server ---
                let authData = null;
                try {
                    authData = await adminService.getUserMeta(userDoc.id);
                    if (authData) {
                        console.log('Firebase Auth data for user:', userDoc.id, authData);
                    } else {
                        console.log('Using Firestore data for user:', userDoc.id);
                    }
                } catch (error) {
                    console.warn('Could not get Firebase Auth data for user:', userDoc.id, error);
                }

                // --- Determine auth type ---
                let authType = 'Unknown';

                // Use Firebase Auth data if available
                if (authData && authData.providerId) {
                    switch (authData.providerId) {
                        case 'password': authType = 'Email'; break;
                        case 'phone': authType = 'Phone'; break;
                        case 'google.com': authType = 'Google'; break;
                        case 'apple.com': authType = 'Apple'; break;
                        default: authType = authData.providerId;
                    }
                }
                // Fallback to Firestore data
                else if (data.providerData && Array.isArray(data.providerData) && data.providerData.length > 0) {
                    const provider = data.providerData[0].providerId;
                    switch (provider) {
                        case 'password': authType = 'Email'; break;
                        case 'phone': authType = 'Phone'; break;
                        case 'google.com': authType = 'Google'; break;
                        case 'apple.com': authType = 'Apple'; break;
                        default: authType = provider;
                    }
                } else if (data.email) {
                    authType = 'Email';
                } else if (data.phoneNumber || data.phone) {
                    authType = 'Phone';
                }

                users.push({
                    id: userDoc.id,
                    uid: userDoc.id,
                    ...data,
                    // Use Firebase Auth data if available, otherwise Firestore data
                    authType: authType,
                    isActive,
                    status,
                    daysSinceActivity,
                    lastActivityDate: lastActivityDate?.toISOString() ?? null,
                    // Use Firebase Auth creation time if available, otherwise Firestore
                    createdAt: authData?.creationTime || (createdAt?.toISOString() ?? null),
                    // Use Firebase Auth last sign-in if available
                    lastSignInTime: authData?.lastSignInTime || null,
                    emailVerified: authData?.emailVerified || data.emailVerified || false,
                    disabled: authData?.disabled || data.disabled || false,
                    // Firebase Auth specific data
                    firebaseAuthCreatedAt: authData?.creationTime || null,
                    firebaseAuthLastSignIn: authData?.lastSignInTime || null,
                });
            }

            return users;
        } catch (error) {
            console.error('Error getting users:', error);
            if (error.code === 'permission-denied') {
                console.warn('Permission denied: ensure Firestore security rules allow admin reads.');
                return [];
            }
            throw error;
        }
    }

    // ✅ Get user by Firestore ID
    async getUserById(userId) {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        return userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null;
    }

    // ✅ Create new user in Firestore
    async createUser(userData) {
        const usersRef = collection(db, 'users');
        const docRef = await addDoc(usersRef, {
            ...userData,
            createdAt: Timestamp.now(),
            lastLoginAt: null,
            isActive: true
        });
        return docRef.id;
    }

    // ✅ Update user data
    async updateUser(userId, userData) {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            ...userData,
            updatedAt: Timestamp.now()
        });
    }

    // ✅ Delete user (Firestore only)
    async deleteUser(userId, deleteFromAuth = false, authType = 'phone') {
        const userRef = doc(db, 'users', userId);
        await deleteDoc(userRef);

        if (deleteFromAuth) {
            const result = await adminService.deleteUserFromAuth(userId);
            if (!result.success) {
                console.error(`Failed to delete user ${userId} from Firebase Auth:`, result.message);
                if (result.details) {
                    console.error('Details:', result.details);
                }
            } else {
                console.log(`User ${userId} deleted from Firebase Auth:`, result.message);
            }
        }

        console.log(`User ${userId} deleted from Firestore`);
    }

    // ✅ Get last app access from audit logs
    async getLastAppAccessFromAuditLogs(userId) {
        try {
            await this.waitForAuth();

            // Check daily_access_summary subcollection under audit_logs/{userId}
            const dailyAccessRef = collection(db, 'audit_logs', userId, 'daily_access_summaries');
            const dailyAccessQuery = query(
                dailyAccessRef,
                orderBy('date', 'desc'),
                limit(1)
            );

            const snapshot = await getDocs(dailyAccessQuery);
            if (!snapshot.empty) {
                const latestAccess = snapshot.docs[0].data();
                const accessDate = latestAccess.date;

                if (accessDate) {
                    // Handle different date formats
                    const logDate = accessDate.toDate ? accessDate.toDate() : new Date(accessDate);
                    if (!isNaN(logDate.getTime())) {
                        console.log(`Last app access for user ${userId}:`, logDate.toISOString());
                        return logDate;
                    }
                }
            }

            console.log(`No daily access summary found for user ${userId}`);
            return null;
        } catch (error) {
            console.error(`Error getting last app access for user ${userId}:`, error);
            return null;
        }
    }

    // ✅ Get audit logs with filtering - updated for new daily aggregated structure
    async getAuditLogs(options = {}) {
        try {
            await this.waitForAuth();

            const {
                dataType,
                severity,
                startDate,
                endDate,
                limit = 100000  // High limit for getting all logs
            } = options;

            console.log('🔍 Starting audit logs fetch with options:', options);
            console.log('📅 Date filter details:', {
                startDate: startDate,
                endDate: endDate,
                startDateType: typeof startDate,
                endDateType: typeof endDate,
                severity: severity
            });

            let allLogs = [];

            // Get all users to check their audit logs
            const usersRef = collection(db, 'users');
            const usersSnapshot = await getDocs(usersRef);

            console.log(`👥 Found ${usersSnapshot.docs.length} users`);

            // Known dataTypes to check
            const knownDataTypes = [
                'general',
                'food_entry',
                'mood_entry',
                'hobby_entry',
                'carbon_entry',
                'utilities',
                'partners',
                'partner_interactions',
                'features',
                'daily_access_summaries'
            ];

            // Get logs from each user's audit log subcollections
            for (const userDoc of usersSnapshot.docs) {
                const userId = userDoc.id;
                console.log(`🔍 Checking audit logs for user: ${userId}`);

                // Check each dataType subcollection
                for (const dataTypeName of knownDataTypes) {
                    // Skip if specific dataType filter is applied and doesn't match
                    if (dataType && dataType !== dataTypeName) {
                        continue;
                    }

                    try {
                        const dataTypeRef = collection(db, 'audit_logs', userId, dataTypeName);
                        let queryConstraints = [];

                        // Apply date filters first (where must come before orderBy in Firestore)
                        if (startDate) {
                            const startDateKey = new Date(startDate);
                            startDateKey.setHours(0, 0, 0, 0);
                            queryConstraints.push(where('date', '>=', startDateKey));
                        }

                        if (endDate) {
                            const endDateKey = new Date(endDate);
                            endDateKey.setHours(23, 59, 59, 999);
                            queryConstraints.push(where('date', '<=', endDateKey));
                        }

                        // Add orderBy after where clauses (only if we have date filters)
                        if (startDate || endDate) {
                            queryConstraints.push(orderBy('date', 'desc'));
                        }

                        const dataTypeQuery = queryConstraints.length > 0
                            ? query(dataTypeRef, ...queryConstraints)
                            : dataTypeRef;
                        const dataTypeSnapshot = await getDocs(dataTypeQuery);

                        console.log(`📝 ${dataTypeName} logs for ${userId}: ${dataTypeSnapshot.docs.length} docs`);

                        dataTypeSnapshot.docs.forEach(doc => {
                            const docData = doc.data();
                            console.log(`📄 ${dataTypeName} doc for ${userId}:`, {
                                id: doc.id,
                                date: docData.date,
                                userId: docData.userId,
                                dataType: docData.dataType
                            });

                            // Handle daily_access_summaries differently
                            if (dataTypeName === 'daily_access_summaries') {
                                const docDate = docData.date?.toDate ? docData.date.toDate() : new Date(docData.date);
                                const lastAccess = docData.lastAccess?.toDate ? docData.lastAccess.toDate() : new Date(docData.lastAccess);

                                allLogs.push({
                                    id: doc.id,
                                    userId: userId,
                                    dataType: 'daily_access_summaries',
                                    type: 'DAILY_ACCESS',
                                    action: 'Daily Access Summary',
                                    description: `Access count: ${docData.totalAccessCount}`,
                                    screenName: 'Daily Summary',
                                    severity: 'LOW',
                                    timestamp: lastAccess || docDate,
                                    metadata: {
                                        appVersion: docData.appVersion,
                                        platform: docData.platform,
                                        totalAccessCount: docData.totalAccessCount,
                                        firstAccess: docData.firstAccess,
                                        lastAccess: docData.lastAccess,
                                        date: docData.date,
                                        primaryLocation: docData.primaryLocation
                                    }
                                });
                            } else {
                                // Handle other dataTypes with events structure
                                const events = docData.events || {};

                                Object.entries(events).forEach(([eventId, eventData]) => {
                                    // Apply severity filter at event level
                                    if (severity && eventData.severity !== severity) {
                                        return;
                                    }

                                    const eventTimestamp = eventData.timestamp?.toDate ? eventData.timestamp.toDate() : new Date(eventData.timestamp);

                                    allLogs.push({
                                        id: eventId,
                                        userId: userId,
                                        dataType: dataTypeName,
                                        type: eventData.type,
                                        action: eventData.action,
                                        description: eventData.description || '',
                                        screenName: eventData.screenName || '',
                                        severity: eventData.severity,
                                        timestamp: eventTimestamp,
                                        metadata: {
                                            docId: doc.id,
                                            date: docData.date,
                                            createdAt: docData.createdAt,
                                            lastUpdated: docData.lastUpdated
                                        }
                                    });
                                });
                            }
                        });

                    } catch (error) {
                        console.warn(`Could not fetch ${dataTypeName} for user ${userId}:`, error.message);
                    }
                }
            }

            // Sort all logs by timestamp
            allLogs.sort((a, b) => {
                const aTime = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
                const bTime = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
                return bTime - aTime;
            });

            // Apply limit only if specified and return pagination info
            const totalCount = allLogs.length;
            const hasMore = totalCount > limit;
            const paginatedLogs = hasMore ? allLogs.slice(0, limit) : allLogs;

            console.log(`📊 Retrieved ${paginatedLogs.length} of ${totalCount} audit logs total (limit: ${limit})`);

            return paginatedLogs;

        } catch (error) {
            console.error('Error loading audit logs:', error);
            return [];
        }
    }

    // ✅ Delete audit log(s)
    async deleteAuditLogs(logIds, userId, dataType) {
        try {
            await this.waitForAuth();

            if (!logIds || logIds.length === 0) {
                throw new Error('No log IDs provided');
            }

            console.log('🗑️ Deleting audit logs:', { logIds, userId, dataType });

            // Group events by document (since multiple events can be in one document)
            const eventsByDoc = {};
            logIds.forEach(logIdObj => {
                const docId = logIdObj.docId;
                const eventId = logIdObj.id;
                if (!eventsByDoc[docId]) {
                    eventsByDoc[docId] = [];
                }
                eventsByDoc[docId].push(eventId);
            });

            let totalDeleted = 0;

            // Process each document
            for (const docId in eventsByDoc) {
                const eventIds = eventsByDoc[docId];

                try {
                    // Get the document that contains these events
                    const docRef = doc(db, 'audit_logs', userId, dataType, docId);
                    const docSnapshot = await getDoc(docRef);

                    if (!docSnapshot.exists()) {
                        console.warn(`Document ${docId} not found`);
                        continue;
                    }

                    const docData = docSnapshot.data();
                    const events = docData.events || {};

                    console.log('Document events before deletion:', Object.keys(events).length);

                    // Remove the specified events from the map
                    eventIds.forEach(eventId => {
                        if (events[eventId]) {
                            delete events[eventId];
                            totalDeleted++;
                        }
                    });

                    console.log('Document events after deletion:', Object.keys(events).length);

                    // Update the document with the modified events map
                    await updateDoc(docRef, {
                        events: events,
                        lastUpdated: Timestamp.now()
                    });

                    console.log(`✅ Deleted ${eventIds.length} event(s) from document ${docId}`);
                } catch (docError) {
                    console.error(`Error processing document ${docId}:`, docError);
                }
            }

            console.log(`✅ Total deleted: ${totalDeleted} audit log(s)`);
            return { success: true, deletedCount: totalDeleted, errors: [] };
        } catch (error) {
            console.error('Error deleting audit logs:', error);
            return { success: false, deletedCount: 0, errors: [error.message] };
        }
    }

    // ✅ Get available audit log data types
    async getAvailableAuditDataTypes() {
        try {
            await this.waitForAuth();

            const availableTypes = [
                'general',
                'food_entry',
                'mood_entry',
                'hobby_entry',
                'carbon_entry',
                'utilities',
                'partners',
                'partner_interactions',
                'features',
                'daily_access_summaries'
            ];

            // Check if these data types have any logs
            const usersRef = collection(db, 'users');
            const usersSnapshot = await getDocs(usersRef);

            const confirmedTypes = [];

            for (const dataType of availableTypes) {
                let hasData = false;

                for (const userDoc of usersSnapshot.docs) {
                    try {
                        const logsRef = collection(db, 'audit_logs', userDoc.id, dataType);
                        const logsQuery = query(logsRef, limit(1));
                        const snapshot = await getDocs(logsQuery);

                        if (!snapshot.empty) {
                            hasData = true;
                            break;
                        }
                    } catch (error) {
                        console.warn(`Could not check ${dataType} logs for user ${userDoc.id}:`, error.message);
                    }
                }

                if (hasData) {
                    confirmedTypes.push(dataType);
                }
            }

            return confirmedTypes;
        } catch (error) {
            console.error('Error getting available audit data types:', error);
            return ['daily_access_summaries', 'general']; // Return default types
        }
    }

    // ✅ Partner Management Methods
    async getPartners() {
        try {
            await this.waitForAuth();

            const partnersRef = collection(db, 'partners');
            const snapshot = await getDocs(partnersRef);

            const partners = [];
            for (const docSnapshot of snapshot.docs) {
                // Ignore the 'category' document
                if (docSnapshot.id === 'category') {
                    continue;
                }

                const data = docSnapshot.data();

                // Skip documents that don't have required partner fields
                if (!data.name || data.name === 'Unknown Partner' || data.name === 'category') {
                    continue;
                }

                // Get category information
                let categoryName = 'Unknown';
                let categoryColor = 'grey';

                if (data.categoryId) {
                    try {
                        const categoryDoc = await getDoc(doc(db, 'partners', 'categories', 'list', data.categoryId));
                        if (categoryDoc.exists()) {
                            const categoryData = categoryDoc.data();
                            categoryName = categoryData.name || 'Unknown';
                            categoryColor = categoryData.color || 'grey';
                        }
                    } catch (error) {
                        console.warn(`Could not fetch category for partner ${docSnapshot.id}:`, error);
                    }
                }

                partners.push({
                    id: docSnapshot.id,
                    name: data.name,
                    description: data.description,
                    websiteUrl: data.websiteUrl,
                    categoryId: data.categoryId,
                    category: categoryName,
                    color: categoryColor,
                    isActive: data.isActive,
                    logoUrl: data.logoUrl,
                    addedAt: data.addedAt,
                    updatedAt: data.updatedAt
                });
            }

            return partners;
        } catch (error) {
            console.error('Error getting partners:', error);
            return [];
        }
    }

    async getPartnerCategoriesWithColors() {
        try {
            await this.waitForAuth();

            const categoriesRef = collection(db, 'partners', 'categories', 'list');
            const snapshot = await getDocs(categoriesRef);

            return snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    name: data.name || '',
                    color: data.color || 'grey',
                    icon: data.icon || 'category'
                };
            });
        } catch (error) {
            console.error('Error getting partner categories:', error);
            return [];
        }
    }

    async createPartner(partnerData) {
        try {
            await this.waitForAuth();

            const { name, description, websiteUrl, categoryId, isActive, logoFile } = partnerData;

            // Create partner document
            const partnerRef = collection(db, 'partners');
            const docRef = await addDoc(partnerRef, {
                name,
                description,
                websiteUrl,
                categoryId,
                isActive,
                addedAt: serverTimestamp()
            });

            // Upload logo if provided
            if (logoFile) {
                const logoPath = `partners/${docRef.id}.${logoFile.name.split('.').pop()}`;
                const logoRef = ref(storage, logoPath);
                await uploadBytes(logoRef, logoFile);
                const logoUrl = await getDownloadURL(logoRef);

                // Update partner with logo URL
                await updateDoc(docRef, { logoUrl });
            }

            console.log('Partner created successfully:', docRef.id);
            return docRef.id;
        } catch (error) {
            console.error('Error creating partner:', error);
            throw error;
        }
    }

    async updatePartner(partnerId, updateData) {
        try {
            await this.waitForAuth();

            const { logoFile, ...otherData } = updateData;

            // Update partner document
            const partnerRef = doc(db, 'partners', partnerId);
            await updateDoc(partnerRef, {
                ...otherData,
                updatedAt: serverTimestamp()
            });

            // Upload new logo if provided
            if (logoFile) {
                const logoPath = `partners/${partnerId}.${logoFile.name.split('.').pop()}`;
                const logoRef = ref(storage, logoPath);
                await uploadBytes(logoRef, logoFile);
                const logoUrl = await getDownloadURL(logoRef);

                // Update partner with new logo URL
                await updateDoc(partnerRef, { logoUrl });
            }

            console.log('Partner updated successfully:', partnerId);
        } catch (error) {
            console.error('Error updating partner:', error);
            throw error;
        }
    }

    async deletePartner(partnerId) {
        try {
            await this.waitForAuth();

            // Delete partner document
            const partnerRef = doc(db, 'partners', partnerId);
            await deleteDoc(partnerRef);

            // Try to delete logo from storage (ignore if not found)
            try {
                const logoRef = ref(storage, `partners/${partnerId}.png`);
                await deleteObject(logoRef);
            } catch (logoError) {
                console.log('Logo not found in storage, continuing...');
            }

            console.log('Partner deleted successfully:', partnerId);
        } catch (error) {
            console.error('Error deleting partner:', error);
            throw error;
        }
    }

    async getPartnerClickStats(partnerId, days = 30) {
        try {
            await this.waitForAuth();

            const startDate = new Date();
            startDate.setDate(startDate.getDate() - days);
            startDate.setHours(0, 0, 0, 0);

            // Query the analytics collection for fast statistics
            const statsRef = collection(db, 'partner_clicks', partnerId, 'daily');
            const statsSnapshot = await getDocs(query(
                statsRef,
                where('date', '>=', startDate),
                orderBy('date', 'desc')
            ));

            let totalClicks = 0;
            const uniqueUsers = new Set();
            const dailyStats = {};

            statsSnapshot.docs.forEach(doc => {
                const data = doc.data();
                const clicks = data.clicks || 0;
                totalClicks += clicks;

                // Add unique users
                const users = data.uniqueUsers || [];
                users.forEach(userId => uniqueUsers.add(userId));

                // Add to daily stats
                const date = data.date.toDate();
                const dateKey = date.toISOString().split('T')[0];
                dailyStats[dateKey] = clicks;
            });

            return {
                totalClicks,
                uniqueUsers: uniqueUsers.size,
                dailyStats,
                period: days
            };
        } catch (error) {
            console.error('Error getting partner click stats:', error);
            return {
                totalClicks: 0,
                uniqueUsers: 0,
                dailyStats: {},
                period: days
            };
        }
    }

    // ✅ Get analytics data
    async getAnalyticsData(period = '7d') {
        try {
            await this.waitForAuth();

            // Calculate date range
            const now = new Date();
            let startDate = new Date();

            switch (period) {
                case '1d':
                    startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                    break;
                case '7d':
                    startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    break;
                case '30d':
                    startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    break;
                case '90d':
                    startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                    break;
                default:
                    startDate = new Date(0);
            }

            // Get all users
            const usersRef = collection(db, 'users');
            const usersSnapshot = await getDocs(usersRef);
            const totalUsers = usersSnapshot.docs.length;

            // Calculate active users (users with activity in the period)
            let activeUsers = 0;
            let newUsers = 0;
            for (const userDoc of usersSnapshot.docs) {
                const data = userDoc.data();
                const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt);

                // Check if user created in period
                if (createdAt >= startDate) {
                    newUsers++;
                }

                // Check if user has activity in period via audit logs
                try {
                    const dailyAccessRef = collection(db, 'audit_logs', userDoc.id, 'daily_access_summaries');
                    const accessQuery = query(
                        dailyAccessRef,
                        where('date', '>=', startDate),
                        limit(1)
                    );
                    const accessSnapshot = await getDocs(accessQuery);

                    if (!accessSnapshot.empty) {
                        activeUsers++;
                    }
                } catch (err) {
                    console.warn('Could not check daily access for user:', err);
                }
            }

            // Get audit log counts
            const auditLogs = await this.getAuditLogs({
                startDate: startDate,
                endDate: now,
                limit: 1000
            });

            const totalLogs = auditLogs.length;
            const todayLogs = auditLogs.filter(log => {
                const logDate = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                return logDate.toDateString() === now.toDateString();
            }).length;

            const errorLogs = auditLogs.filter(log =>
                log.severity?.includes('ERROR') || log.severity === 'HIGH' || log.severity === 'CRITICAL'
            ).length;

            const criticalLogs = auditLogs.filter(log =>
                log.severity === 'CRITICAL'
            ).length;

            // Get feature usage statistics
            const featureUsage = [];
            const featureAccessLogs = auditLogs.filter(log => log.dataType === 'features');
            const featureCounts = {};

            featureAccessLogs.forEach(log => {
                const featureName = log.action || 'Unknown';
                if (!featureCounts[featureName]) {
                    featureCounts[featureName] = 0;
                }
                featureCounts[featureName]++;
            });

            Object.entries(featureCounts).forEach(([feature, count]) => {
                featureUsage.push({
                    feature,
                    usage: Math.min(100, (count / totalUsers) * 100),
                    users: count
                });
            });

            // Calculate error stats
            const errorTypes = {};
            const errorsLogs = auditLogs.filter(log =>
                log.severity?.includes('ERROR') || log.type?.includes('ERROR')
            );

            errorsLogs.forEach(log => {
                const errorType = log.action || 'Unknown Error';
                if (!errorTypes[errorType]) {
                    errorTypes[errorType] = 0;
                }
                errorTypes[errorType]++;
            });

            // Calculate retention rate
            const retentionRate = totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0;

            return {
                userStats: {
                    totalUsers,
                    activeUsers,
                    newUsers,
                    retentionRate
                },
                auditStats: {
                    totalLogs,
                    todayLogs,
                    errorLogs,
                    criticalLogs
                },
                featureUsage,
                errorStats: {
                    totalErrors: errorLogs,
                    errorTypes: Object.entries(errorTypes).map(([type, count]) => ({ type, count }))
                },
                timeSeriesData: []
            };
        } catch (error) {
            console.error('Error getting analytics data:', error);
            return {
                userStats: {
                    totalUsers: 0,
                    activeUsers: 0,
                    newUsers: 0,
                    retentionRate: 0
                },
                auditStats: {
                    totalLogs: 0,
                    todayLogs: 0,
                    errorLogs: 0,
                    criticalLogs: 0
                },
                featureUsage: [],
                errorStats: {
                    totalErrors: 0,
                    errorTypes: []
                },
                timeSeriesData: []
            };
        }
    }
}

export default new FirestoreService();
